import { defineCatalogue } from '../../i18n';
import type { ApiFailure } from '../../lib/http/errors';

/**
 * Shared across the whole `/keys` area: the list screen's two tables and
 * every dialog over a key (`659:19979`, `665:20548`, `665:20906`). One
 * catalogue rather than one per file, matching `../operations/messages.ts`
 * -- a key's own identity (`KeyIdentity.tsx`) and the grace control
 * (`grace-period.tsx`) keep their own, narrower catalogues, since neither
 * changed shape here.
 */
export const ptMessages = {
  'active.title': 'Chaves ativas',
  'active.header.name': 'Nome',
  'active.header.usedBy': 'Usada por',
  'active.header.registered': 'Registrada',
  'active.header.lastRotated': 'Última troca',
  'active.header.actions': 'Ações',
  'active.empty': 'Nenhuma chave registrada ainda.',

  'usedBy.count.other': '{n} operações',
  'usedBy.count.one': '{n} operação',
  'usedBy.none': 'Sem uso',

  'lastRotated.relative': 'há {days}',
  'lastRotated.pending': 'Ainda não ativada.',

  'action.new': 'Nova chave',
  'action.renew': 'Renovar',
  'action.retire': 'Aposentar',
  'aria.renew': 'Renovar {name}',
  'aria.retire': 'Aposentar {name}',

  'retired.title': 'Chaves aposentadas',
  'retired.header.name': 'Nome',
  'retired.header.retiredAt': 'Aposentada',
  'retired.header.deletedAt': 'Apagada em',
  'retired.empty': 'Nenhuma chave aposentada ainda.',

  'new.title': 'Nova chave',
  'new.subtitle': 'Depois de salvo, só os quatro últimos caracteres aparecem.',
  'new.name.label': 'Nome',
  'new.name.placeholder': 'OpenRouter · principal',
  'new.provider.label': 'Provedor',
  'new.provider.placeholder': 'Escolha o provedor desta chave',
  'new.secret.label': 'Segredo',
  'new.submit': 'Salvar chave',

  'renew.title': 'Renovar a chave',
  'renew.usedBy.label': 'Usada por',
  'renew.secret.label': 'Novo segredo',
  'renew.grace.legend': 'Carência do segredo antigo',
  'renew.submit': 'Renovar chave',

  'key.error.tooShort': 'O segredo deve ter pelo menos 16 caracteres.',
  'key.error.whitespace': 'O segredo não pode começar nem terminar com espaço.',
  'key.error.conflict': 'Esta chave já está registrada.',
  'key.error.generic': 'Não foi possível salvar esta chave; tente novamente.',
} as const;

export const enMessages: Record<keyof typeof ptMessages, string> = {
  'active.title': 'Active keys',
  'active.header.name': 'Name',
  'active.header.usedBy': 'Used by',
  'active.header.registered': 'Registered',
  'active.header.lastRotated': 'Last rotated',
  'active.header.actions': 'Actions',
  'active.empty': 'No keys registered yet.',

  'usedBy.count.other': '{n} operations',
  'usedBy.count.one': '{n} operation',
  'usedBy.none': 'Unused',

  'lastRotated.relative': '{days} ago',
  'lastRotated.pending': 'Not yet activated.',

  'action.new': 'New key',
  'action.renew': 'Renew',
  'action.retire': 'Retire',
  'aria.renew': 'Renew {name}',
  'aria.retire': 'Retire {name}',

  'retired.title': 'Retired keys',
  'retired.header.name': 'Name',
  'retired.header.retiredAt': 'Retired',
  'retired.header.deletedAt': 'Deleted on',
  'retired.empty': 'No keys retired yet.',

  'new.title': 'New key',
  'new.subtitle': 'Once saved, only the last four characters appear.',
  'new.name.label': 'Name',
  'new.name.placeholder': 'OpenRouter · main',
  'new.provider.label': 'Provider',
  'new.provider.placeholder': 'Choose the provider this key is for',
  'new.secret.label': 'Secret',
  'new.submit': 'Save key',

  'renew.title': 'Renew the key',
  'renew.usedBy.label': 'Used by',
  'renew.secret.label': 'New secret',
  'renew.grace.legend': 'Grace period for the old secret',
  'renew.submit': 'Renew key',

  'key.error.tooShort': 'The secret must be at least 16 characters.',
  'key.error.whitespace': 'The secret cannot start or end with a space.',
  'key.error.conflict': 'This key is already registered.',
  'key.error.generic': 'Could not save this key; try again.',
};

export const keysCatalogue = defineCatalogue(ptMessages, enMessages);
export const kt = keysCatalogue.t;
export const kp = keysCatalogue.plural;

/** api answers a secret its validator refuses with a bare-string 422 (no
 *  field), and the secret is the only input that validator reads. */
export function displayKeyFailure(failure: ApiFailure | null): ApiFailure | null {
  if (failure === null) return null;
  if (failure.kind === 'invalid' && (failure.field === null || failure.field === 'secret')) {
    const whitespace = failure.message.includes('whitespace');
    const message = kt(whitespace ? 'key.error.whitespace' : 'key.error.tooShort');
    return { kind: 'invalid', field: 'secret', message };
  }
  if (failure.kind === 'conflict') {
    return { kind: 'invalid', field: null, message: kt('key.error.conflict') };
  }
  if (failure.kind === 'server') {
    return { kind: 'invalid', field: null, message: kt('key.error.generic') };
  }
  return failure;
}
